import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DotsSixVertical, Pencil, Trash } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import Icon from 'components/Icon/Icon';
import Button from 'components/Button/Button';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import { Level, upLevel } from 'components/Modals/Modal/type';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';
import {
  ICreateOptionData,
  IUpdateOptionData,
} from 'types/ConfigurationQuestion.types';
import { ITestExtraPriceOption } from 'types/Configuration.types';
import Draggable from 'components/DragAndDrop/Draggable/Draggable';
import { moveItem } from 'pages/DealerPanel/DealerPanelModelsPage/DealerPanelCategory/ModelConfiguration/QuestionsManager/reorder';
import { OptionFormModal } from 'pages/DealerPanel/DealerPanelModelsPage/DealerPanelCategory/ModelConfiguration/QuestionsManager/OptionFormModal/OptionFormModal';
import {
  DragHandle,
  EmptyState,
  IconButton,
  InlineButtonRow,
  List,
  PriceText,
  Row,
  RowActions,
  RowLabel,
  RowMain,
  RowSubInfo,
  SlugBadge,
  ToggleCell,
  ToggleLabel,
} from 'pages/DealerPanel/DealerPanelModelsPage/DealerPanelCategory/ModelConfiguration/QuestionsManager/OptionsManager.styled';

interface ILocalOptionsManagerProps {
  options: ITestExtraPriceOption[];
  setOptions: (options: ITestExtraPriceOption[]) => void;
  currency: string;
  level: Level;
}

// In-memory options editor for the test/preview flow. All changes mutate local
// state only and never hit the options CRUD endpoints.
export const LocalOptionsManager = ({
  options,
  setOptions,
  currency,
  level,
}: ILocalOptionsManagerProps) => {
  const { t } = useTranslation();
  const formatMoney = useFormatMoney(currency);

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deletionIndex, setDeletionIndex] = useState<number | null>(null);

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingIndex(null);
  };

  const handleAdd = (data: ICreateOptionData) => {
    setOptions([
      ...options,
      {
        label: data.label,
        slug: data.slug,
        price: data.price,
        is_default: false,
      },
    ]);
    closeForm();
  };

  const handleEdit = (data: IUpdateOptionData) => {
    if (editingIndex === null) return;
    setOptions(
      options.map((option, index) =>
        index === editingIndex
          ? {
              ...option,
              label: data.label ?? option.label,
              price: data.price ?? option.price,
              is_default: data.is_default ?? option.is_default,
            }
          : option
      )
    );
    closeForm();
  };

  const handleSetDefault = (index: number) => {
    // Toggle: turning a default off is allowed (a question may have no default).
    const newIsDefault = !options[index].is_default;
    setOptions(
      options.map((option, current) => {
        if (current === index) {
          return { ...option, is_default: newIsDefault };
        }
        return newIsDefault ? { ...option, is_default: false } : option;
      })
    );
  };

  const handleDragHover = (from: number, to: number) =>
    setOptions(moveItem(options, from, to));

  const editingOption = editingIndex !== null ? options[editingIndex] : null;

  return (
    <>
      {options.length ? (
        <List>
          {options.map((option, index) => (
            <Draggable
              key={index}
              type="local-option"
              index={index}
              id={index}
              draggedId={null}
              setDraggedId={() => undefined}
              onDragHover={handleDragHover}
              isDragDisabled={options.length < 2}
            >
              <Row>
                <DragHandle>
                  <Icon
                    svg={DotsSixVertical}
                    size={18}
                    color={COLORS.BORDER}
                    weight="bold"
                  />
                </DragHandle>
                <RowMain>
                  <RowLabel>{option.label}</RowLabel>
                  <RowSubInfo>
                    <SlugBadge>{option.slug}</SlugBadge>
                    <PriceText>{formatMoney(option.price)}</PriceText>
                  </RowSubInfo>
                </RowMain>
                <RowActions>
                  <ToggleCell>
                    <ToggleLabel>{t('Default')}</ToggleLabel>
                    <ToggleSwitch
                      isChecked={option.is_default}
                      onClick={() => handleSetDefault(index)}
                    />
                  </ToggleCell>
                  <IconButton
                    onClick={() => {
                      setEditingIndex(index);
                      setIsFormOpen(true);
                    }}
                  >
                    <Icon svg={Pencil} size={20} color={COLORS.GREEN} />
                  </IconButton>
                  <IconButton onClick={() => setDeletionIndex(index)}>
                    <Icon svg={Trash} size={20} color={COLORS.RED} />
                  </IconButton>
                </RowActions>
              </Row>
            </Draggable>
          ))}
        </List>
      ) : (
        <EmptyState>{t('No options yet')}</EmptyState>
      )}

      <InlineButtonRow>
        <Button
          secondary
          label={t('Add option')}
          height="36rem"
          onClick={() => {
            setEditingIndex(null);
            setIsFormOpen(true);
          }}
        />
      </InlineButtonRow>

      <OptionFormModal
        isOpen={isFormOpen}
        level={upLevel(level)}
        currency={currency}
        optionForEditing={editingOption}
        onClose={closeForm}
        onAdd={handleAdd}
        onEdit={handleEdit}
        isLoading={false}
      />

      <DangerModal
        level={upLevel(level)}
        isOpen={deletionIndex !== null}
        setIsOpen={(open) => !open && setDeletionIndex(null)}
        title={t('Delete option')}
        description={t('Are you sure you want to delete this option?')}
        submit={{
          onClick: () => {
            if (deletionIndex !== null) {
              setOptions(options.filter((_, index) => index !== deletionIndex));
            }
            setDeletionIndex(null);
          },
          text: t('Confirm'),
          disabled: false,
        }}
        cancel={{ onClick: () => setDeletionIndex(null) }}
      />
    </>
  );
};
